/**
 * Tenant utility functions
 * 
 * Helper functions to transform tenant data from API format to component formats
 */

import type { GetTenantResponse } from "@/openapi/generated";
import { CONTACT_DATA } from "@/config/contact";

/**
 * Transform tenant API data to ContactData format for ContactForm component
 * All contact data comes from admin/tenant API. Uses CONTACT_DATA only as fallback when tenantData is null.
 */
export const transformTenantToContactData = (
  tenantData: GetTenantResponse | null | undefined
): { name: string; city: string; phone: string; website: string; address: string; postalCode: string } => {
  if (!tenantData) {
    return {
      name: CONTACT_DATA.name,
      city: CONTACT_DATA.city,
      phone: CONTACT_DATA.phone,
      website: CONTACT_DATA.website,
      address: CONTACT_DATA.address,
      postalCode: CONTACT_DATA.postalCode,
    };
  }

  // When tenant exists but a field is empty, use empty string so UI shows "-" or no link (not placeholder text)
  const addressParts = [
    tenantData.addressLine1,
    tenantData.addressLine2,
    tenantData.addressLine3,
  ].filter(Boolean);
  const address = addressParts.length > 0 ? addressParts.join(", ") : "";

  const website = tenantData.siteUrl || "";

  return {
    name: tenantData.displayName || tenantData.name || CONTACT_DATA.name,
    city: tenantData.city || "",
    phone: tenantData.phoneNumber || "",
    website,
    address,
    postalCode: tenantData.zipCode || "",
  };
};

/**
 * Get tenant logo URL
 * TODO: Use tenantData.logo when API supports logo upload
 */
export const getTenantLogo = (
  tenantData: GetTenantResponse | null | undefined
): string => {
  // TODO: Return tenantData.logo when API supports it
  return CONTACT_DATA.logo;
};

/**
 * Get tenant display name
 */
export const getTenantDisplayName = (
  tenantData: GetTenantResponse | null | undefined
): string => {
  if (!tenantData) {
    return CONTACT_DATA.name;
  }
  return tenantData.displayName || tenantData.name || CONTACT_DATA.name;
};

